import { useState } from "react";
import { Link } from "react-router-dom";

import Fab from "../../components/Fab";
import FuelPumpEasterEgg from "../../components/FuelPumpEasterEgg";
import { useFuelStations } from "../../api/fuelStations";
import { useFormOptions, useOverview } from "../../api/queries";
import { useActiveCar } from "../../auth/useActiveCar";
import { useAuth } from "../../auth/AuthProvider";
import NewEntryModal from "./NewEntryModal";
import OverviewCarHeader from "./OverviewCarHeader";
import StatCardGrid from "./StatCardGrid";
import LastFillupSection from "./LastFillupSection";

export default function OverviewPage() {
  const { cars, activeCar, hasCars, isLoading: activeCarLoading } = useActiveCar();
  const overview = useOverview(0);
  const formOptions = useFormOptions();
  const fuelStations = useFuelStations();
  const { me } = useAuth();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const open = () => setIsModalOpen(true);
  const close = () => setIsModalOpen(false);

  if (activeCarLoading || overview.isLoading) {
    return (
      <section className="page-section">
        <p className="empty-state">Loading…</p>
      </section>
    );
  }

  if (!hasCars) {
    return (
      <section className="page-section">
        <div className="empty-state">
          <p>You don't have any cars yet.</p>
          <p>Add one in the Garage to start logging fuel.</p>
          <Link to="/garage" className="button button--primary">
            Go to Garage
          </Link>
        </div>
      </section>
    );
  }

  if (overview.isError || !overview.data) {
    return (
      <section className="page-section">
        <p className="empty-state">
          Could not load the overview: {overview.error?.message ?? "unknown error"}.
        </p>
      </section>
    );
  }

  const data = overview.data;

  return (
    <>
      <OverviewCarHeader lastOdometerDatetime={data.last_odometer_datetime} />

      <StatCardGrid
        month_liters={data.month_liters}
        month_spend={data.month_spend}
        month_km_driven={data.month_km_driven}
        month_avg_l_per_100km={data.month_avg_l_per_100km}
        month_avg_cost_per_100km={data.month_avg_cost_per_100km}
        last_month_liters={data.last_month_liters}
        last_month_spend={data.last_month_spend}
        last_month_km_driven={data.last_month_km_driven}
        last_month_avg_l_per_100km={data.last_month_avg_l_per_100km}
        last_month_avg_cost_per_100km={data.last_month_avg_cost_per_100km}
      />

      {data.last_fuel_entry && (
        <LastFillupSection entry={data.last_fuel_entry} />
      )}

      {me?.fuel_pump_easter_egg ? (
        <FuelPumpEasterEgg onDeliver={open} modalOpen={isModalOpen} />
      ) : (
        <Fab iconName="plus" onClick={open} aria-label="New entry" />
      )}

      {formOptions.data ? (
        <NewEntryModal
          isOpen={isModalOpen}
          onClose={close}
          formOptions={formOptions.data}
          cars={cars}
          activeCarId={activeCar?.id ?? null}
          fuelStations={fuelStations.data?.stations ?? []}
          recentFuelStationId={fuelStations.data?.recent_fuel_station_id ?? null}
        />
      ) : null}
    </>
  );
}
